<template>
  <div>
    <div v-if="setup.step === 1" class="d-flex flex-column g-4">
      <HttpContentView :endpoint="utilities.path('communities/@/server-addons/latest', info.community.id)">
        <template v-slot:content="{ data, loading }">
          <h2 class="mb-0 d-flex align-items-center g-3">
            <i class="fas fa-puzzle-piece text-primary"></i>
            <span>{{ translate('server_addon') }}</span>
          </h2>
          <a v-if="data.fileUrl" class="btn btn-soft btn-primary w-100" :href="data.fileUrl" target="_blank">
            <div class="d-flex align-items-center justify-content-center g-2">
              <i class="fas fa-download"></i>
              <span>{{ translate("download") }}</span>
            </div>
          </a>
          <div class="card">
            <div class="card-body">
              <RichTextView :content="JSON.parse(JSON.stringify(data.instructions).replaceAll(':token', setup.server.token))" :features="instructionsFeatures"/>
              <VideoTutorialButton v-if="translate('tutorials.videos.setup.' + info.community?.type.key) !== ('tutorials.videos.setup.' + info.community?.type.key)" class="w-100 mt-4" :title="translate('view_setup_tutorial')" :url="translate('tutorials.videos.setup.' + info.community?.type.key)"/>
            </div>
          </div>
          <ButtonView type="normal" color="primary" @click="setup.step = 2">
            {{ translate("continue") }}
          </ButtonView>
          <SpinnerView :active="loading" color="primary"/>
        </template>
      </HttpContentView>
    </div>
    <div v-else-if="setup.step === 2" class="d-flex flex-column g-4">
      <div class="row align-items-center g-2 m-4">
        <div class="col-4">
          <div class="d-flex justify-content-center">
            <div class="d-flex flex-column text-center g-3">
              <i class="fa-duotone fa-server text-primary" style="font-size: 3em"></i>
              <span v-if="!setup.server.connected" class="bg-secondary rounded-3 px-2 py-1">{{ translate("server") }}</span>
              <span v-else class="bg-secondary rounded-3 px-2 py-1">{{ setup.server.ip }}</span>
            </div>
          </div>
        </div>
        <div class="col-4">
          <div class="d-flex justify-content-center">
            <div class="d-flex flex-column text-center g-3">
              <i v-if="!setup.server.connected" class="fas fa-ellipsis" style="font-size: 2em; opacity: 0.5"></i>
              <i v-else class="fa-duotone fa-check-circle text-success" style="font-size: 2em"></i>
              <span v-if="!setup.server.connected" class="rounded-3 px-2 py-1">{{ translate("community.server.connection.pending") }}</span>
              <span v-else class="rounded-3 px-2 py-1">{{ translate("community.server.connected") }}</span>
            </div>
          </div>
        </div>
        <div class="col-4">
          <div class="d-flex justify-content-center">
            <div class="d-flex flex-column justify-content-center text-center g-3">
              <div>
                <img src="@/assets/img/vactar.png" style="height: 3em; max-width: 3em"/>
              </div>
              <div class="bg-secondary rounded-3 px-2 py-1">Vactar</div>
            </div>
          </div>
        </div>
      </div>
      <div class="d-flex align-items-center g-4">
        <ButtonView v-if="!setup.server.connected" type="normal" color="secondary" class="w-100" @click="setup.step = 1">
          <div class="d-flex align-items-center justify-content-center g-3">
            <i class="fas fa-arrow-left"></i>
            <span>{{ translate("back") }}</span>
          </div>
        </ButtonView>
        <ButtonView type="normal" color="primary" class="w-100" :disabled="!setup.server.connected" @click="emit('complete')">
          {{ translate("continue") }}
        </ButtonView>
      </div>
    </div>
  </div>
</template>

<script setup>
import {ButtonView, HttpContentView, RichTextView, richTextFeatures, SpinnerView, utilities} from "@niftybit/vue";
import VideoTutorialButton from "@/components/other/VideoTutorialButton.vue";
import {onMounted, onUnmounted, ref} from "vue";
import info from "../../../info";
import eventBus from "@/eventBus";

const props = defineProps({
  server: Object
});

const emit = defineEmits(["complete"]);

const setup = ref({
  step: 1,
  server: props.server
});

function onSocketEvent(data) {
  if (data.event === "community.server.connected" && setup.value && setup.value.server && data.data.server.id === setup.value.server.id) {
    setup.value.server.connected = true;
    setup.value.server.ip = data.data.server.ip;
    setup.value.server.addonVersion = data.data.server.addonVersion;
  }
}

onMounted(() => {
  eventBus.on("socket:event", onSocketEvent);
});

onUnmounted(() => {
  eventBus.unsubscribe("socket:event", onSocketEvent);
});

const instructionsFeatures = [
  richTextFeatures.paragraph.create(),
  richTextFeatures.text.create(),
  richTextFeatures.hardBreak.create(),
  richTextFeatures.heading.create(),
  richTextFeatures.textStyle.create(),
  richTextFeatures.codeBlock.create(),
  richTextFeatures.bulletList.create(),
  richTextFeatures.orderedList.create(),
  richTextFeatures.infoBox.create(),
  richTextFeatures.blockQuote.create(),
  richTextFeatures.textAlign.create(),
  richTextFeatures.code.create(),
  richTextFeatures.strikethrough.create(),
  richTextFeatures.bold.create(),
  richTextFeatures.underline.create(),
  richTextFeatures.italic.create(),
  richTextFeatures.link.create(),
  richTextFeatures.videoEmbed.create(),
];

</script>